/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useTracker } from 'bundles/page/lib/event-pulse/react';
import ChipButton from 'bundles/search-common/components/experiments/filters2024/ChipButton';
import CourseraPlusFilterChipButton from 'bundles/search-common/components/experiments/filters2024/components/CourseraPlusFilterChipButton';
import {
  FILTER_DROPDOWN_KEYS,
  getQuickFilterProps,
  getSingleFilterValueForKey,
} from 'bundles/search-common/components/experiments/filters2024/utils';
import { SEARCH_FILTERS_INDEX_ID } from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { useShowCourseraTier } from 'bundles/search-common/providers/LearnerProvider';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import useStableSearchResults from 'bundles/search-common/utils/useStableSearchResults';
import { createFilterKeyToOptionsMap, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';

const styles = {
  chipWrapper: css`
    display: flex;
    align-items: center;
    gap: 2px;
  `,
};

const scrollOnApply = () => {
  scrollUpIfNecessary({ mustBeScrolledPastThisElementId: 'search-results-header-wrapper' });
};

export const QuickFilterChips = (): React.ReactElement => {
  const quickFilters = getQuickFilterProps();
  const { addFilters, removeFilters, activeIndex, setFiltersByFacet } = useSearch();
  const searchResults = useStableSearchResults();
  const { facets, facetFilters: userFilters } = searchResults || {};
  const { facets: allFilters } = useStableSearchResults(SEARCH_FILTERS_INDEX_ID) ?? {};

  const results = useStableSearchResults();
  const shouldShowPlusChipButton = useShowCourseraTier();
  const track = useTracker();
  const isCPlusActive =
    shouldShowPlusChipButton && getSingleFilterValueForKey(IS_PART_OF_COURSERA_PLUS, results?.facetFilters) === 'true';

  const filterKeyToValuesMap = React.useMemo(
    () => createFilterKeyToOptionsMap(facets, userFilters, FILTER_DROPDOWN_KEYS, allFilters),
    [allFilters, userFilters, facets]
  );

  return (
    <div css={styles.chipWrapper}>
      {shouldShowPlusChipButton && (
        <CourseraPlusFilterChipButton
          isActive={isCPlusActive}
          onClick={() => {
            setFiltersByFacet(
              activeIndex,
              IS_PART_OF_COURSERA_PLUS,
              isCPlusActive ? [] : [`${IS_PART_OF_COURSERA_PLUS}:true`]
            );
            scrollOnApply();
            track('apply_filter', {
              filter: isCPlusActive ? [] : [`${IS_PART_OF_COURSERA_PLUS}:true`],
              filterSource: 'quick_filter',
            });
          }}
        />
      )}
      {quickFilters.map((quickFilter) => {
        const isInProductTypeFilters = filterKeyToValuesMap.productTypeDescription?.some(
          (filter) => filter && filter?.label === quickFilter.label
        );
        const isInProductDifficultyLevelFilters = filterKeyToValuesMap.productDifficultyLevel?.some(
          (filter) => filter && filter?.label === quickFilter.label
        );

        const isQuickFilterActive = results?.facetFilters?.some((filter) => filter.includes(quickFilter.values[0]));

        // Need to set false and reassign later specifically so the buttons don't appear disabled at first
        let isQuickFilterDisabled = false;
        if (isInProductTypeFilters !== undefined || isInProductDifficultyLevelFilters !== undefined) {
          isQuickFilterDisabled = !(isInProductTypeFilters || isInProductDifficultyLevelFilters);
        }

        return (
          <ChipButton
            key={quickFilter.label}
            isActive={isQuickFilterActive}
            isDisabled={isQuickFilterDisabled}
            prefix={quickFilter.prefix}
            onClick={() => {
              if (isQuickFilterActive) {
                removeFilters(activeIndex, quickFilter.values);
              } else {
                addFilters(activeIndex, quickFilter.values);
              }
              scrollOnApply();

              track('apply_filter', {
                filter: isQuickFilterActive ? [] : quickFilter.values,
                filterSource: 'quick_filter',
              });
            }}
          >
            {quickFilter.label}
          </ChipButton>
        );
      })}
    </div>
  );
};

export default QuickFilterChips;
